import React, { useState, useEffect } from 'react';
import API from '../../lib/api';
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    AreaChart, Area
} from 'recharts';
import { Activity, TrendingUp } from 'lucide-react';

export default function OverByOverChart({ matchId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!matchId) return;
        const fetchData = async () => {
            try {
                const [bbbRes, adjRes] = await Promise.all([
                    API.get(`/live/ball-by-ball/${matchId}`),
                    API.get(`/intelligence/adjusted-prediction/${matchId}`),
                ]);
                setData({
                    bbb: bbbRes.data,
                    adjusted: adjRes.data,
                });
            } catch { }
            setLoading(false);
        };
        fetchData();
        const iv = setInterval(fetchData, 15000);
        return () => clearInterval(iv);
    }, [matchId]);

    if (loading) return null;

    const overs = data?.bbb?.adjustments || [];
    const hasData = overs.length > 0;

    // Build chart data: cumulative win% change
    let cumAdj = 50;
    const baseAdj = data?.adjusted?.adjusted_win_pct_a || 50;
    cumAdj = baseAdj;

    const chartData = overs.map(o => {
        cumAdj = Math.min(95, Math.max(5, cumAdj + o.adjustment));
        return {
            over: `Ov ${o.over}`,
            winPctA: Math.round(cumAdj * 10) / 10,
            winPctB: Math.round((100 - cumAdj) * 10) / 10,
            runs: o.runs,
            wickets: o.wickets,
        };
    });

    return (
        <div className="glass-card p-5 border border-white/5" data-testid="over-by-over-chart">
            <div className="flex items-center gap-2 mb-4">
                <Activity className="w-4 h-4 text-cyan-400" />
                <h3 className="heading-primary text-sm text-white">OVER-BY-OVER WIN PROBABILITY</h3>
                {hasData && (
                    <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/20 text-green-400 tech-mono ml-auto">
                        LIVE — {overs.length} OVERS
                    </span>
                )}
            </div>

            {hasData ? (
                <div>
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={chartData}>
                            <defs>
                                <linearGradient id="winGradA" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#00E5FF" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#00E5FF" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                            <XAxis dataKey="over" tick={{ fill: '#555', fontSize: 9 }} />
                            <YAxis domain={[0, 100]} tick={{ fill: '#555', fontSize: 9 }} />
                            <Tooltip
                                contentStyle={{ background: '#0a0a0a', border: '1px solid #222', borderRadius: 8, fontSize: 12 }}
                                labelStyle={{ color: '#fff' }}
                                formatter={(value, name) => [
                                    `${value}%`,
                                    name === 'winPctA' ? 'Home' : 'Away'
                                ]}
                            />
                            <Area type="monotone" dataKey="winPctA" stroke="#00E5FF" strokeWidth={2}
                                fill="url(#winGradA)" dot={{ fill: '#00E5FF', r: 3 }} />
                            <Line type="monotone" dataKey="winPctB" stroke="#FF2D95" strokeWidth={1.5}
                                dot={{ fill: '#FF2D95', r: 2 }} strokeDasharray="5 5" />
                        </AreaChart>
                    </ResponsiveContainer>
                    <div className="flex items-center justify-center gap-6 mt-2">
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-1 rounded bg-cyan-400" />
                            <span className="text-[10px] text-white/30">Home Win%</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-1 rounded bg-pink-500 opacity-60" style={{ borderBottom: '1px dashed #FF2D95' }} />
                            <span className="text-[10px] text-white/30">Away Win%</span>
                        </div>
                    </div>
                    <div className="mt-3 text-[10px] text-white/15 tech-mono text-center">
                        Total adjustment: {data.bbb.total_adjustment > 0 ? '+' : ''}{data.bbb.total_adjustment}% | Avg deviation: {data.bbb.avg_deviation}
                    </div>
                </div>
            ) : (
                <div className="text-center py-8">
                    <TrendingUp className="w-8 h-8 text-white/10 mx-auto mb-2" />
                    <div className="text-sm text-white/20">Win probability chart will appear during live matches</div>
                    <div className="text-[10px] text-white/10 mt-1">Tracks over-by-over win% changes in real-time</div>
                </div>
            )}
        </div>
    );
}
